import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Search, MapPin, ShoppingCart, Filter, Package, Handshake, ShieldCheck, FileCheck, XCircle, Eye, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';
import { tStatus, tCategory } from '@/i18n';
import SPAActions from '@/components/SPAActions';

export default function BuyerDashboard() {
  const { t } = useTranslation();
  const [minerals, setMinerals] = useState([]);
  const [myOffers, setMyOffers] = useState([]);
  const [myDeals, setMyDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [currentSummary, setCurrentSummary] = useState(null);
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();

  const fetchMinerals = useCallback(async () => {
    try {
      const params = {};
      if (selectedCategory !== 'all') params.category = selectedCategory;
      if (searchTerm) params.search = searchTerm;
      const response = await axios.get(`${API_URL}/minerals`, { params });
      setMinerals(response.data);
    } catch { toast.error(t('error.generic')); }
    finally { setLoading(false); }
  }, [selectedCategory, searchTerm, t]);

  const fetchDeals = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/deals/my-deals`, { headers: getAuthHeader() });
      setMyDeals(response.data);
    } catch {}
  }, [getAuthHeader]);

  const fetchMyOffers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/offers/buyer/my-offers`, { headers: getAuthHeader() });
      setMyOffers(response.data);
    } catch {}
  }, [getAuthHeader]);

  useEffect(() => { fetchMinerals(); fetchDeals(); fetchMyOffers(); }, [fetchMinerals, fetchDeals, fetchMyOffers]);

  const handleCancelOffer = async (offerId) => {
    if (!window.confirm(t('buyer.cancelOfferConfirm'))) return;
    try {
      await axios.post(`${API_URL}/offers/${offerId}/cancel`, {}, { headers: getAuthHeader() });
      toast.success(t('buyer.offerCancelled'));
      fetchMyOffers();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error.generic'));
    }
  };

  const handleViewSummary = async (dealId) => {
    try {
      const res = await axios.get(`${API_URL}/deals/${dealId}/summary`, { headers: getAuthHeader() });
      setCurrentSummary(res.data);
      setSummaryOpen(true);
    } catch { toast.error(t('error.generic')); }
  };

  return (
    <div className="min-h-screen bg-[#0B1120]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-1 font-['Outfit']" data-testid="buyer-dashboard-title">{t('buyer.title')}</h1>
          <p className="text-[#94A3B8]">{t('buyer.subtitle')}</p>
        </div>

        <Tabs defaultValue="browse" className="w-full">
          <TabsList className="mb-6 bg-[#121A2F] border border-[#1E293B]">
            <TabsTrigger value="browse" data-testid="browse-tab" className="data-[state=active]:bg-[#F59E0B] data-[state=active]:text-[#0B1120]">
              <Package className="w-4 h-4 mr-2" /> {t('buyer.tabs.marketplace')} ({minerals.length})
            </TabsTrigger>
            <TabsTrigger value="offers" data-testid="offers-tab" className="data-[state=active]:bg-[#F59E0B] data-[state=active]:text-[#0B1120]">
              <ShoppingCart className="w-4 h-4 mr-2" /> {t('buyer.tabs.myOffers')} ({myOffers.length})
            </TabsTrigger>
            <TabsTrigger value="deals" data-testid="deals-tab" className="data-[state=active]:bg-[#F59E0B] data-[state=active]:text-[#0B1120]">
              <Handshake className="w-4 h-4 mr-2" /> {t('buyer.tabs.myDeals')} ({myDeals.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="browse">
            <div className="bg-[#121A2F] rounded-xl border border-[#1E293B] p-6 mb-8">
              <form onSubmit={(e) => { e.preventDefault(); fetchMinerals(); }} className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#64748B] w-5 h-5" />
                  <Input type="text" placeholder={t('buyer.searchPlaceholder')} data-testid="search-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-[#0B1120] border-[#1E293B] text-white placeholder:text-[#64748B] focus:border-[#F59E0B] focus:ring-[#F59E0B]/50" />
                </div>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-full md:w-48 bg-[#0B1120] border-[#1E293B] text-white" data-testid="category-filter">
                    <Filter className="w-4 h-4 mr-2 text-[#64748B]" /><SelectValue placeholder={t('buyer.allCategories')} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121A2F] border-[#1E293B]">
                    {['all', 'coltan', 'copper', 'cobalt', 'lithium', 'manganese', 'chromium', 'iron_ore', 'gemstones', 'gold', 'diamonds', 'rare_earth', 'bauxite', 'other'].map(v => (
                      <SelectItem key={v} value={v} className="text-[#94A3B8] focus:bg-white/5 focus:text-white">
                        {v === 'all' ? t('buyer.allCategories') : tCategory(v)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="submit" className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0B1120] font-semibold" data-testid="search-button">{t('common.search')}</Button>
              </form>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F59E0B]"></div></div>
            ) : minerals.length === 0 ? (
              <div className="text-center py-20">
                <ShoppingCart className="w-16 h-16 text-[#1E293B] mx-auto mb-4" />
                <p className="text-xl text-[#94A3B8]">{t('buyer.empty.minerals')}</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {minerals.map((mineral) => (
                  <div key={mineral.id} onClick={() => navigate(`/mineral/${mineral.id}`)} data-testid={`mineral-card-${mineral.id}`}
                    className="bg-[#121A2F] rounded-xl border border-[#1E293B] overflow-hidden hover:-translate-y-1 hover:border-[#F59E0B]/30 hover:shadow-[0_8px_30px_rgba(245,158,11,0.1)] transition-all duration-300 cursor-pointer">
                    <div className="aspect-video bg-[#1A2440] overflow-hidden">
                      {mineral.images?.[0] ? (
                        <img src={mineral.images[0]} alt={mineral.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Package className="w-12 h-12 text-[#1E293B]" /></div>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex items-center justify-between mb-2">
                        <span className="bg-[#F59E0B]/10 text-[#F59E0B] px-2.5 py-0.5 rounded-full text-xs font-medium border border-[#F59E0B]/20">
                          {tCategory(mineral.category)}
                        </span>
                        <span className="text-xl font-bold text-white">${mineral.price}<span className="text-sm font-normal text-[#64748B]">/{mineral.unit}</span></span>
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2">{mineral.title}</h3>
                      <p className="text-[#64748B] text-sm mb-3 line-clamp-2">{mineral.description}</p>
                      <div className="flex items-center text-xs text-[#64748B]"><MapPin className="w-3 h-3 mr-1" />{mineral.location}</div>
                      <div className="mt-3 flex items-center gap-2 text-xs text-[#64748B] flex-wrap">
                        <span>{t('detail.seller')}: <span className="font-medium text-[#94A3B8]">{mineral.seller_name}</span></span>
                        {mineral.seller_verified ? (
                          <span className="inline-flex items-center gap-0.5 text-[#10B981]" data-testid={`verified-badge-${mineral.id}`}>
                            <ShieldCheck className="w-3.5 h-3.5" /> {t('detail.verifiedSeller')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-0.5 text-[#F59E0B]" data-testid={`unverified-badge-${mineral.id}`}>
                            <ShieldCheck className="w-3.5 h-3.5" /> {t('detail.unverifiedSeller')}
                          </span>
                        )}
                        {mineral.listing_status === 'pending_verification' && (
                          <span className="inline-flex items-center gap-0.5 text-[#F59E0B] bg-[#F59E0B]/10 px-1.5 py-0.5 rounded-full border border-[#F59E0B]/20" data-testid={`pending-listing-${mineral.id}`}>
                            {t('common.pending')}
                          </span>
                        )}
                        {mineral.verified_documents_count > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-[#3B82F6]" data-testid={`docs-badge-${mineral.id}`}>
                            <FileCheck className="w-3.5 h-3.5" /> {mineral.verified_documents_count}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="offers" data-testid="offers-content">
            {myOffers.length === 0 ? (
              <div className="text-center py-20 bg-[#121A2F] rounded-xl border border-[#1E293B]">
                <ShoppingCart className="w-16 h-16 text-[#1E293B] mx-auto mb-4" />
                <p className="text-xl text-[#94A3B8]">{t('buyer.empty.offers')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {myOffers.map((offer) => (
                  <div key={offer.id} className="bg-[#121A2F] rounded-xl border border-[#1E293B] p-6" data-testid={`my-offer-card-${offer.id}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-bold text-white">{offer.mineral_title}</h3>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            offer.status === 'pending' ? 'bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/20' :
                            offer.status === 'accepted' ? 'bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20' :
                            offer.status === 'rejected' ? 'bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/20' :
                            offer.status === 'cancelled' ? 'bg-[#64748B]/10 text-[#64748B] border border-[#64748B]/20' :
                            'bg-[#3B82F6]/10 text-[#3B82F6] border border-[#3B82F6]/20'
                          }`} data-testid={`offer-status-${offer.id}`}>{tStatus(offer.status)}</span>
                        </div>
                        <p className="text-[#94A3B8] mb-1">{t('detail.seller')}: <span className="font-medium text-white">{offer.seller_name}</span></p>
                        <p className="text-sm text-[#64748B] mb-1">{t('common.quantity')}: {offer.quantity}</p>
                        {offer.message && <p className="text-sm text-[#64748B] italic mt-1">"{offer.message}"</p>}
                        {offer.counter_offer && (
                          <p className="text-sm text-[#3B82F6] mt-1">{t('seller.counter')}: <span className="font-semibold">${offer.counter_offer}</span></p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-3">
                        <div className="text-right">
                          <p className="text-sm text-[#64748B]">{t('buyer.offerAmount')}</p>
                          <p className="text-2xl font-bold text-[#F59E0B]">${offer.offer_price}</p>
                        </div>
                        {(offer.status === 'pending' || offer.status === 'countered') && (
                          <Button
                            onClick={() => handleCancelOffer(offer.id)}
                            data-testid={`cancel-offer-${offer.id}`}
                            className="bg-transparent border border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10"
                          >
                            <XCircle className="w-4 h-4 mr-1.5" /> {t('buyer.cancelOffer')}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="deals" data-testid="deals-content">
            {myDeals.length === 0 ? (
              <div className="text-center py-20 bg-[#121A2F] rounded-xl border border-[#1E293B]">
                <Handshake className="w-16 h-16 text-[#1E293B] mx-auto mb-4" />
                <p className="text-xl text-[#94A3B8]">{t('buyer.empty.deals')}</p>
              </div>
            ) : (
              <div className="bg-[#121A2F] rounded-xl border border-[#1E293B] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#1A2440] border-b border-[#1E293B]">
                      <tr>
                      {[t('detail.specs'), t('detail.seller'), t('common.price'), t('common.quantity'), 'Total', t('common.status'), t('common.date'), ''].map((h, i) => (
                          <th key={i} className="px-6 py-4 text-left text-xs uppercase tracking-wider font-semibold text-[#94A3B8]">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {myDeals.map((deal) => (
                        <tr key={deal.id} className="border-b border-[#1E293B] hover:bg-white/[0.02] transition-colors" data-testid={`deal-row-${deal.id}`}>
                          <td className="px-6 py-4 text-sm font-medium text-white">{deal.mineral_title}</td>
                          <td className="px-6 py-4 text-sm text-[#94A3B8]">{deal.seller_name}</td>
                          <td className="px-6 py-4 text-sm text-white">${deal.agreed_price.toLocaleString()}</td>
                          <td className="px-6 py-4 text-sm text-[#94A3B8]">{deal.quantity}</td>
                          <td className="px-6 py-4 text-sm font-medium text-[#F59E0B]">${deal.total_value.toLocaleString()}</td>
                          <td className="px-6 py-4">
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                              deal.status === 'completed' ? 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20' :
                              deal.status === 'verified' ? 'bg-[#3B82F6]/10 text-[#3B82F6] border-[#3B82F6]/20' :
                              deal.status === 'in_progress' ? 'bg-[#8B5CF6]/10 text-[#8B5CF6] border-[#8B5CF6]/20' :
                              deal.status === 'cancelled' ? 'bg-[#EF4444]/10 text-[#EF4444] border-[#EF4444]/20' :
                              'bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/20'
                            }`}>{tStatus(deal.status)}</span>
                          </td>
                          <td className="px-6 py-4 text-sm text-[#64748B]">{new Date(deal.created_at).toLocaleDateString()}</td>
                          <td className="px-6 py-4">
                            {deal.status !== 'pending_verification' && (
                              <div className="flex flex-wrap items-center gap-2">
                                <Button onClick={() => navigate(`/deals/${deal.id}`)} data-testid={`open-deal-${deal.id}`} size="sm" className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0B1120] font-semibold">
                                  Open Deal
                                </Button>
                                <Button onClick={() => handleViewSummary(deal.id)} data-testid={`view-summary-${deal.id}`} size="sm" className="bg-[#1A2440] text-white border border-[#1E293B] hover:bg-white/5">
                                  <Eye className="w-3.5 h-3.5 mr-1" /> {t('common.view')}
                                </Button>
                                <SPAActions deal={deal} onUpdated={fetchDeals} />
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <Dialog open={summaryOpen} onOpenChange={setSummaryOpen}>
          <DialogContent className="bg-[#121A2F] border-[#1E293B] max-w-lg" data-testid="deal-summary-dialog">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#F59E0B]" /> Deal Agreement Summary
              </DialogTitle>
            </DialogHeader>
            {currentSummary && (
              <div className="mt-4 bg-[#0B1120] rounded-xl border border-[#1E293B] overflow-hidden">
                <div className="bg-[#1A2440] px-5 py-3 border-b border-[#1E293B]">
                  <p className="text-xs text-[#64748B] uppercase tracking-wider">Deal #{currentSummary.deal_id?.slice(0, 8)}</p>
                  <p className="text-lg font-bold text-white">{currentSummary.mineral_title}</p>
                </div>
                <div className="divide-y divide-[#1E293B]">
                  {[
                    ['Mineral Type', currentSummary.mineral_type],
                    ['Quantity', `${currentSummary.quantity} ${currentSummary.unit}`],
                    ['Price per Unit', `$${currentSummary.agreed_price_per_unit?.toLocaleString()} ${currentSummary.currency}`],
                    ['Total Value', `$${currentSummary.total_value?.toLocaleString()} ${currentSummary.currency}`],
                    ['Buyer', currentSummary.buyer_name],
                    ['Seller', currentSummary.seller_name],
                    ['Location', currentSummary.location],
                    ['Grade', currentSummary.grade],
                    ['Delivery Terms', currentSummary.delivery_terms],
                    ['Payment Terms', currentSummary.payment_terms],
                  ].filter(([, val]) => val).map(([label, value]) => (
                    <div key={label} className="flex justify-between px-5 py-3">
                      <span className="text-sm text-[#64748B]">{label}</span>
                      <span className={`text-sm font-medium text-right max-w-[60%] ${label === 'Total Value' ? 'text-[#F59E0B]' : 'text-white'}`}>{value}</span>
                    </div>
                  ))}
                </div>
                <div className="px-5 py-3 bg-[#1A2440] border-t border-[#1E293B]">
                  <p className="text-xs text-[#64748B]">Generated: {new Date(currentSummary.generated_at).toLocaleString()}</p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
